import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Calendar, CheckCircle, AlertCircle, X, Info, RefreshCw } from 'lucide-react';
import autoPayApi, { AutoPaySettings as AutoPaySettingsType, PaymentMethod } from '../../api/autoPay';
import PageHeader from '../../components/PageHeader';

export default function AutoPaySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AutoPaySettingsType>({
    enabled: false,
    paymentMethodId: null,
    paymentMethodNickname: '',
    frequency: 'MONTHLY',
    daysBefore: 3,
    amount: 0,
    notifyBeforePayment: true,
    notifyAfterPayment: true,
    notifyOnFailure: true
  });
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadAutoPaySettings();
  }, []);

  const loadAutoPaySettings = async () => {
    setLoading(true);
    try {
      // Fetch settings and payment methods from API
      const [settingsData, methodsData] = await Promise.all([
        autoPayApi.getSettings(),
        autoPayApi.getPaymentMethods()
      ]);

      // Ensure proper defaults to avoid null values in controlled inputs
      setSettings({
        enabled: settingsData.enabled ?? false,
        paymentMethodId: settingsData.paymentMethodId ?? null,
        paymentMethodNickname: settingsData.paymentMethodNickname ?? '',
        frequency: settingsData.frequency ?? 'MONTHLY',
        daysBefore: settingsData.daysBefore ?? 3,
        amount: settingsData.amount ?? 0,
        notifyBeforePayment: settingsData.notifyBeforePayment ?? true,
        notifyAfterPayment: settingsData.notifyAfterPayment ?? true,
        notifyOnFailure: settingsData.notifyOnFailure ?? true
      });
      setPaymentMethods(methodsData);
    } catch (err) {
      console.error('Failed to load auto-pay settings:', err);
      // Fallback to mock data if API fails (for development)
      setPaymentMethods([
        {
          id: '1',
          type: 'BANK_ACCOUNT',
          nickname: 'Commonwealth Bank',
          details: 'BSB: 062-000 | Account: ****3456',
          isDefault: true
        },
        {
          id: '2',
          type: 'BPAY',
          nickname: 'Rentry BPay',
          details: 'Biller: 123456 | Ref: ****7890',
          isDefault: false
        }
      ]);
      setSettings({
        enabled: false,
        paymentMethodId: null,
        paymentMethodNickname: '',
        frequency: 'MONTHLY',
        daysBefore: 3,
        amount: 0,
        notifyBeforePayment: true,
        notifyAfterPayment: true,
        notifyOnFailure: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setError('');

    if (settings.enabled && !settings.paymentMethodId) {
      setError('Please select a payment method');
      return;
    }

    setSaving(true);
    try {
      const response = await autoPayApi.saveSettings(settings);

      if (response.success) {
        setSuccess(response.message || 'Auto-pay settings saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoPay = async (enabled: boolean) => {
    const newSettings = { ...settings, enabled };
    setSettings(newSettings);

    if (!enabled) {
      // Disable auto-pay via API
      try {
        await autoPayApi.disableAutoPay();
      } catch (err) {
        console.error('Failed to disable auto-pay:', err);
      }
      setSuccess('');
      setError('');
    }
  };

  const handleSelectPaymentMethod = (methodId: string) => {
    const method = paymentMethods.find(m => m.id === methodId);
    setSettings({
      ...settings,
      paymentMethodId: methodId,
      paymentMethodNickname: method?.nickname || ''
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="Auto-Pay Settings"
        subtitle="Set up automatic rent payments to never miss a due date"
        icon={<RefreshCw className="w-8 h-8" />}
        backPath="/dashboard"
      />

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-green-800">{success}</div>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-red-800">{error}</div>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Enable/Disable Auto-Pay */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-primary mb-2">Enable Auto-Pay</h2>
                <p className="text-gray-600 text-sm">
                  Automatically pay your rent on schedule without manual intervention
                </p>
              </div>
              <button
                onClick={() => handleToggleAutoPay(!settings.enabled)}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                  settings.enabled ? 'bg-secondary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                    settings.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {settings.enabled && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Auto-pay is enabled</span>
                </div>
              </div>
            )}
          </div>

          {settings.enabled && (
            <>
              {/* Payment Method Selection */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-primary">Payment Method</h2>
                  <button
                    onClick={() => navigate('/renter/payment-methods')}
                    className="text-sm text-secondary hover:text-primary font-medium"
                  >
                    Manage Methods
                  </button>
                </div>

                {paymentMethods.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No payment methods found</p>
                    <button
                      onClick={() => navigate('/renter/payment-methods')}
                      className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
                    >
                      Add Payment Method
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <label
                        key={method.id}
                        className={`flex items-center gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          settings.paymentMethodId === method.id
                            ? 'border-secondary bg-secondary/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="autoPayMethod"
                          value={method.id}
                          checked={settings.paymentMethodId === method.id}
                          onChange={() => handleSelectPaymentMethod(method.id)}
                          className="w-4 h-4 text-secondary focus:ring-secondary"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{method.nickname}</div>
                          <div className="text-sm text-gray-600">{method.details}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment Schedule */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-primary mb-4">Payment Schedule</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Frequency
                    </label>
                    <select
                      value={settings.frequency}
                      onChange={(e) => setSettings({ ...settings, frequency: e.target.value as any })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    >
                      <option value="WEEKLY">Weekly</option>
                      <option value="FORTNIGHTLY">Fortnightly</option>
                      <option value="MONTHLY">Monthly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pay Before Due Date
                    </label>
                    <select
                      value={settings.daysBefore}
                      onChange={(e) => setSettings({ ...settings, daysBefore: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    >
                      <option value="0">On due date</option>
                      <option value="1">1 day before</option>
                      <option value="2">2 days before</option>
                      <option value="3">3 days before</option>
                      <option value="5">5 days before</option>
                      <option value="7">7 days before</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Your rent will be automatically paid {settings.daysBefore > 0 ? `${settings.daysBefore} day${settings.daysBefore > 1 ? 's' : ''} before` : 'on'} the due date
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Amount
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-lg">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings.amount ?? ''}
                        onChange={(e) => setSettings({ ...settings, amount: parseFloat(e.target.value) || 0 })}
                        className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent text-lg"
                        placeholder="0.00"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      This amount will be automatically deducted on schedule
                    </p>
                  </div>
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-primary mb-4">Notifications</h2>
                <div className="space-y-3">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.notifyBeforePayment}
                      onChange={(e) => setSettings({ ...settings, notifyBeforePayment: e.target.checked })}
                      className="w-4 h-4 text-secondary focus:ring-secondary rounded"
                    />
                    <span className="text-gray-700">Email me before each payment</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.notifyAfterPayment}
                      onChange={(e) => setSettings({ ...settings, notifyAfterPayment: e.target.checked })}
                      className="w-4 h-4 text-secondary focus:ring-secondary rounded"
                    />
                    <span className="text-gray-700">Notify me after successful payment</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={settings.notifyOnFailure}
                      onChange={(e) => setSettings({ ...settings, notifyOnFailure: e.target.checked })}
                      className="w-4 h-4 text-secondary focus:ring-secondary rounded"
                    />
                    <span className="text-gray-700">Alert me if payment fails</span>
                  </label>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex gap-4">
                <button
                  onClick={handleSaveSettings}
                  disabled={saving || !settings.paymentMethodId}
                  className="flex-1 py-3 px-6 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all shadow-md hover:shadow-lg"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  onClick={() => navigate('/renter/dashboard')}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Sidebar - Info & Benefits */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-semibold text-primary mb-4">Why Use Auto-Pay?</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-gray-900">Never Miss a Payment</div>
                  <div className="text-xs text-gray-600">Automatic payments on schedule</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-gray-900">Peace of Mind</div>
                  <div className="text-xs text-gray-600">Set it and forget it</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-sm text-gray-900">Avoid Late Fees</div>
                  <div className="text-xs text-gray-600">Always pay on time</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Important Information</h3>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li>• You can cancel auto-pay at any time</li>
                  <li>• Ensure sufficient funds are available</li>
                  <li>• You'll receive notifications before each payment</li>
                  <li>• Auto-pay doesn't affect manual payments</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-lg p-6">
            <h3 className="font-semibold text-primary mb-2">Need Help?</h3>
            <p className="text-sm text-gray-700 mb-4">
              Have questions about auto-pay? Contact your property manager for assistance.
            </p>
            <button
              onClick={() => navigate('/messages')}
              className="w-full py-2 px-4 bg-white text-secondary hover:bg-gray-50 rounded-lg font-medium transition-colors"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


